import React, {createContext} from "react";
import useLocalStorage from 'use-local-storage';
import { MeshType } from "../@types/enums";

export const INCH = 'in';
export const FOOT = 'ft';
export const METER = 'm';

export const INCH_SQUARE = 'in2';
export const FOOT_SQUARE = 'ft2';
export const METER_SQUARE = 'm2';

export const LENGTH = 'length';
export const AREA = 'area';

export const ROOF = 'roof';
export const OBSTRUCTION = 'obstruction';
export const TREE = 'tree';

declare type LengthUnit = 'in' | 'ft' | 'm';
declare type AreaUnit = 'in2' | 'ft2' | 'm2';
export interface UnitContextType {
    unit: LengthUnit,
    roofUnit: LengthUnit,
    obstructionUnit: LengthUnit,
    treeUnit: LengthUnit,
    setUnit?: (value: LengthUnit) => void,
    setRoofUnit?: (value: LengthUnit) => void,
    setObstructionUnit?: (value: LengthUnit) => void,
    setTreeUnit?: (value: LengthUnit) => void,
    convertQuantityToUnit: (quantity: number, quantityUnit: LengthUnit | AreaUnit, targetUnit: LengthUnit | AreaUnit) => number,
    convertTypeQuantityToUnit: (type: MeshType.Obst | MeshType.Tree | MeshType.Roof | null, 
        quantity: number, quantityUnit: string) => number,
    renderUnit: (value: any) => LengthUnit,
}

export const UnitContext = createContext<UnitContextType>({
    unit: 'ft',
    roofUnit: 'ft',
    obstructionUnit: 'ft',
    treeUnit: 'ft',
    convertQuantityToUnit: () => 0,
    convertTypeQuantityToUnit: () => 0,
    renderUnit: ()=> 'ft',
});

const unitMap: any = {};
unitMap[INCH] = { name: INCH, toBase: (x: number) => x / 39.37, toUnit: (x: number) => x * 39.37, type: LENGTH };
unitMap[FOOT] = { name: FOOT, toBase: (x: number) => x / 3.28084, toUnit: (x: number)=> x * 3.28084, type: LENGTH };
unitMap[METER] = { name: METER, toBase: (x: number) => x, toUnit: (x: number) => x, type: LENGTH };
unitMap[INCH_SQUARE] = { name: INCH_SQUARE, toBase: (x: number)=> x / 1550, toUnit: (x: number) => x * 1550, type: AREA };
unitMap[FOOT_SQUARE] = { name: FOOT_SQUARE, toBase: (x: number) => x / 10.764, toUnit: (x: number) => x * 10.764, type: AREA };
unitMap[METER_SQUARE] = { name: METER_SQUARE, toBase: (x: number) => x, toUnit: (x: number) => x, type: AREA };

export const UnitProvider = ({children}: any) => {
    const [unit, setUnit] = useLocalStorage<LengthUnit>('unitOfMeasure', 'ft');
    const [roofUnit, setRoofUnit] = useLocalStorage<LengthUnit>('roofUnitOfMeasure', 'ft');
    const [obstructionUnit, setObstructionUnit] = useLocalStorage<LengthUnit>('obstructionUnitOfMeasure', 'ft');
    const [treeUnit, setTreeUnit] = useLocalStorage<LengthUnit>('treeUnitOfMeasure', 'ft');

    const getAreaUnit = (unit: any) => {
        switch (unit.name) {
            case INCH: return unitMap[INCH_SQUARE];
            case FOOT: return unitMap[FOOT_SQUARE];
            case METER: return unitMap[METER_SQUARE];
            default: return null;
        }
    };

    const convertTypeQuantityToUnit = (type: MeshType.Obst | MeshType.Tree | MeshType.Roof | null, quantity: number, quantityUnit: string) => {
        let typeUnit;

        switch (type) {
            case MeshType.Roof:
                typeUnit = roofUnit;
                break;
            case MeshType.Obst:
                typeUnit = obstructionUnit;
                break;
            case MeshType.Tree:
                typeUnit = treeUnit;
                break;
            default:
                typeUnit = unit;
                break;
        }

        const qUnit = unitMap[quantityUnit];
        if (!qUnit) {
            throw new Error(`unknown quantity unit ${quantityUnit}`);
        }

        if (qUnit.type === AREA) {
            typeUnit = getAreaUnit(unitMap[typeUnit]).name;
        }

        return convertQuantityToUnit(quantity, quantityUnit, typeUnit);
    };

    const convertQuantityToUnit = (quantity: number, quantityUnit: string, targetUnit: string) => {
        if (quantityUnit === targetUnit) {
            return quantity;
        }

        const qUnit = unitMap[quantityUnit];
        if (!qUnit) {
            throw new Error(`unknown quantity unit ${quantityUnit}`);
        }

        const tUnit = unitMap[targetUnit];
        if (!tUnit) {
            throw new Error(`unknown target unit ${targetUnit}`);
        }

        if (qUnit.type !== tUnit.type) {
            throw new Error(`cannot convert between ${qUnit.type} and ${tUnit.type}`);
        }

        return tUnit.toUnit(qUnit.toBase(quantity));
    };

    const renderUnit = (type: LengthUnit): LengthUnit => {
        switch (type) {
            case INCH: return 'in';
            case FOOT: return 'ft';
            case METER: return 'm';
            default: return 'ft';
        }
    }

    return (
        <UnitContext.Provider value={{
            unit,
            roofUnit,
            obstructionUnit,
            treeUnit,
            setUnit,
            setRoofUnit,
            setObstructionUnit,
            setTreeUnit,
            convertQuantityToUnit,
            convertTypeQuantityToUnit,
            renderUnit,
        }}>
            {children}
        </UnitContext.Provider>
    );
}